import { FC, useLayoutEffect, useRef, useState } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { dateLocal, DateFormats } from "src/lib/date";
import translate from "src/lib/translate";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { IconExpand } from "src/ui/assets/icons/IconExpand";
import Translate from "src/ui/components/Translate/Translate";

import MealTodayInsightModal from "./MealTodayInsightModal";

const Card = styled.button`
  position: relative;
  z-index: 2;
  margin: 0 0 -20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  overflow: hidden;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 1rem 1rem 0.75rem;
  background: var(--color-white, #fff);
  border-radius: 1.5rem;
  box-shadow:
    -5px 5px 16px rgba(69, 49, 22, 0.08),
    -20px 20px 29px rgba(69, 49, 22, 0.07),
    -46px 45px 39px rgba(69, 49, 22, 0.03),
    -82px 81px 46px rgba(69, 49, 22, 0.01);
`;

const Label = styled.p`
  margin: 0;
  padding-right: 2rem;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;

const InsightWrap = styled.div`
  position: relative;
`;

const Insight = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-size: 1rem;
  line-height: 1.4;
  letter-spacing: -0.16px;
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Fog = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3rem;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    var(--color-white, #fff)
  );
`;

const Footer = styled.p`
  margin: 0.625rem 0 0;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  line-height: 1.33;
`;

const ExpandButton = styled.span`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--Greys-Gray, #aaa7a0);
  line-height: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const MealTodayInsightsCard: FC = () => {
  const [{ todaySummary }] = useBlocNext(MealInsightsCubit);
  const [{ insights }] = useBlocNext(MealsStatsCubit);
  const [modalOpen, setModalOpen] = useState(false);
  const [overflows, setOverflows] = useState(true);
  const insightRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = insightRef.current;
    if (!el) {
      return;
    }
    const measureOverflow = () => {
      if (el.clientHeight === 0) {
        return;
      }
      setOverflows(el.scrollHeight - el.clientHeight > 1);
    };
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [insights]);

  const { mealCount, latestMealAt } = todaySummary;

  const footerParts = [
    latestMealAt
      ? translate("mealInsights.today.updatedAt", {
          time: dateLocal(latestMealAt).format(DateFormats.DISPLAY_TIME)
        })
      : undefined,
    translate("mealInsights.today.mealsLogged", { count: mealCount })
  ].filter(Boolean);

  const footer = footerParts.join(" · ");

  return (
    <>
      <Card
        type="button"
        onClick={() => setModalOpen(true)}
        aria-label={translate("mealInsights.today.expand")}
      >
        <Label>
          <Translate msg="mealInsights.today.insightsLabel" />
        </Label>
        <InsightWrap>
          <Insight ref={insightRef}>{insights}</Insight>
          {overflows && <Fog aria-hidden />}
        </InsightWrap>
        <Footer>{footerParts.join(" · ")}</Footer>
        <ExpandButton aria-hidden>
          <IconExpand />
        </ExpandButton>
      </Card>
      {modalOpen && (
        <MealTodayInsightModal
          insights={insights}
          footer={footer}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default MealTodayInsightsCard;
